/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ParameterDefinition } from '@karya/parameter-specs';
import {
  FileInputProps,
  ListInputProps,
  MultiFileInputProps,
  MultiSelectProps,
  SectionContainerProps,
  SelectProps,
  StringInputProps,
  SwitchProps,
} from '../types/InputComponentProps';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { UseFormReturn } from '../hooks/useForm';
import _ from 'lodash';

export interface DefinitionRendererConfig {
  FormContainer?: React.FC<PropsWithChildren>;
  SectionContainer: React.FC<SectionContainerProps>;
  Input: React.FC<StringInputProps>;
  ListInput: React.FC<ListInputProps>;
  File: React.FC<FileInputProps>;
  MultiFile: React.FC<MultiFileInputProps>;
  Select: React.FC<SelectProps>;
  MultiSelect: React.FC<MultiSelectProps>;
  Switch: React.FC<SwitchProps>;
}

export interface DefinitionRendererProps {
  // Using the any type here to not make any assumptions type of the parameter definitions
  parameter: ParameterDefinition<any>;

  // Using the any type here to not make any assumptions about the form state
  ctx: UseFormReturn<any>['ctx'];
  opts?: Partial<{
    errorId: string;
  }>;
}

export function DefinitionRendererBuilder(Components: DefinitionRendererConfig) {
  const ParameterDefinitionRenderer = React.memo(
    ({ parameter, ctx, opts }: DefinitionRendererProps) => {
      const { id, type, required, label, description, renderer, extraProps } = parameter;
      const {
        form,
        errors,
        handleChange,
        handleListChange,
        handleBooleanChange,
        handleFileChange,
        handleMultiFileChange,
        handleJsonChange,
        renderers,
        setFormField,
        readonlyFields,
        disabledFields,
        setError,
      } = ctx;
      const value = form[id];
      const error = errors[opts?.errorId ?? id] ?? undefined;
      let isRequirementsSatisfied = true;
      let isRequired = required;

      const matchingRenderer = useMemo(() => {
        if (!renderer) {
          return null;
        }
        const match = renderers?.find((curr) => curr.type === renderer) ?? null;
        if (match == null) {
          console.warn(`Renderer with name "${renderer}" was required by parameter with ID "${id}", but was not found`);
        }
        return match;
      }, [id, renderer, renderers]);

      useEffect(() => {
        if (parameter.type === 'fixed' && form[id] !== parameter.value) {
          setFormField(id, parameter.value);
        }
        // @ts-expect-error -- We don't know weather parameter.value exists or not, so we can't type it
      }, [form, id, parameter.type, parameter.value, setFormField]);

      if ('requires' in parameter && parameter.requires) {
        isRequirementsSatisfied = parameter.requires.reduce((acc, [key, reference, conditionType]) => {
          const fieldState = ctx.form[key];
          let isRequirementSatisfied = false; // Default: malformed config fails safely

          const effectiveType = conditionType ?? 'EQUALS';
          switch (effectiveType) {
            case 'EQUALS':
              if (reference == false && fieldState == undefined) {
                isRequirementSatisfied = true;
              } else {
                isRequirementSatisfied = fieldState == reference;
              }
              break;
            case 'NOT_EQUALS':
              isRequirementSatisfied = fieldState != reference;
              break;

            case 'IN':
              if (Array.isArray(reference)) {
                isRequirementSatisfied = reference.includes(fieldState);
              } else {
                console.warn(
                  `Invalid IN requirement for parameter "${id}": expected an array, got ${typeof reference}`,
                );
                isRequirementSatisfied = false;
              }
              break;

            case 'NOT_IN':
              if (Array.isArray(reference)) {
                isRequirementSatisfied = !reference.includes(fieldState);
              } else {
                console.warn(
                  `Invalid NOT_IN requirement for parameter "${id}": expected an array, got ${typeof reference}`,
                );
                isRequirementSatisfied = false;
              }
              break;
          }
          return acc && isRequirementSatisfied;
        }, true);
        if (isRequirementsSatisfied && parameter.requiredIfSatisfied) {
          isRequired = true;
        }
      }

      let isDisabled = !isRequirementsSatisfied || !!disabledFields?.includes(id);
      if (ctx.hideDisabledFields && isDisabled) {
        return null;
      }

      // Show readonly fields even if hideDisabledFields is true
      const isReadonly = !!readonlyFields?.includes(id);
      isDisabled = isDisabled || isReadonly;

      // Base renderer params without setError (for most components)
      let baseRendererParams = {
        id,
        value,
        label,
        error,
        description,
        isRequired,
        isDisabled,
        isInvalid: error !== undefined,
      };

      // Renderer params with setError (only for File components)
      let fileRendererParams = {
        ...baseRendererParams,
        setError,
      };

      if (extraProps) {
        baseRendererParams = { ...baseRendererParams, ...extraProps };
        fileRendererParams = { ...fileRendererParams, ...extraProps };
      }

      if (matchingRenderer) {
        return (
          <matchingRenderer.component
            ctx={ctx}
            extraProps={extraProps}
            rendererProps={matchingRenderer.props}
            parameter={parameter}
            inputProps={baseRendererParams}
            /** @deprecated */
            form={form}
            /** @deprecated */
            errors={errors}
            /** @deprecated */
            setFormField={setFormField}
            /** @deprecated */
            {...baseRendererParams}
            /** @deprecated */
            {...matchingRenderer.props}
          />
        );
      }

      switch (parameter.type) {
        case 'string':
          return (
            <Components.Input
              {...baseRendererParams}
              value={value ?? ''}
              type={type}
              // @ts-expect-error Change handlers have different types that are difficult to handle
              onChange={handleChange}
              long={parameter.long}
              placeholder={parameter.placeholder}
            />
          );
        case 'int':
        case 'float':
        case 'time':
        case 'date':
          // @ts-expect-error Change handlers have different types that are difficult to handle
          return <Components.Input {...baseRendererParams} value={value ?? ''} type={type} onChange={handleChange} />;
        case 'boolean':
          return (
            <Components.Switch {...baseRendererParams} isChecked={value ?? false} onChange={handleBooleanChange} />
          );
        case 'enum':
          return (
            <Components.Select
              {...baseRendererParams}
              value={value ?? ''}
              onChange={setFormField<string>}
              options={parameter.list}
            />
          );
        case 'enum_multi':
          return (
            <Components.MultiSelect
              {...baseRendererParams}
              value={value ?? []}
              onChange={handleListChange}
              options={parameter.list}
            />
          );
        case 'list':
          return (
            <Components.ListInput
              {...baseRendererParams}
              value={value ?? []}
              delimiters={parameter.delimiters}
              onChange={handleListChange}
            />
          );
        case 'file':
          return (
            <Components.File
              {...fileRendererParams}
              onChange={handleFileChange}
              acceptedTypes={parameter.acceptedTypes}
            />
          );
        case 'json_array':
          if (parameter.items) {
            // Open up the array into individual parameters if items are defined
            return (
              <React.Fragment>
                {parameter.items.map((item, index) => {
                  // Generate a unique ID for each item based on index
                  const itemId = `${parameter.id}.${item.id}`;
                  const errorId = `${parameter.id}.${index}`;
                  return (
                    <ParameterDefinitionRenderer
                      ctx={ctx}
                      parameter={{ ...item, id: itemId }}
                      key={itemId}
                      opts={{ errorId }}
                    />
                  );
                })}
              </React.Fragment>
            );
          } else {
            return <Components.File {...fileRendererParams} value={value ?? []} onChange={handleJsonChange} />;
          }
        case 'json_object':
          return <Components.File {...fileRendererParams} value={value ?? {}} onChange={handleJsonChange} />;
        case 'json':
          return <Components.File {...fileRendererParams} value={value ?? null} onChange={handleJsonChange} />;
        case 'multi_file':
          return (
            <Components.MultiFile
              {...fileRendererParams}
              value={value ?? []}
              showFileNames={parameter.showFileNames}
              onChange={handleMultiFileChange}
            />
          );
        case 'fixed':
          return null;
      }
    },
    (prev, next) => {
      if (prev === next) return true;
      const {
        ctx: { form: prevForm, errors: prevErrors },
        parameter: prevParameter,
      } = prev;

      const {
        ctx: { form: nextForm, errors: nextErrors },
        parameter: nextParameter,
      } = next;

      if (prevParameter.renderer != undefined) {
        return false;
      }

      if (!_.isEqual(prevParameter, nextParameter)) {
        return false;
      }

      const prevId = prevParameter.id;
      const nextId = nextParameter.id;
      if (!_.isEqual(prevForm[prevId], nextForm[nextId])) {
        return false;
      }

      let isRequirementsChanged = false;
      if ('requires' in nextParameter) {
        nextParameter.requires?.forEach(([id]) => {
          if (!_.isEqual(prevForm[id as string], nextForm[id as string])) {
            isRequirementsChanged = true;
            return;
          }
        });
      }

      if (isRequirementsChanged) {
        return false;
      }

      if (prevParameter.type === 'json_array' && prevParameter.items) {
        const itemsChanged = prevParameter.items.some(
          (item) => prevForm[`${nextId}.${item.id}`] !== nextForm[`${nextId}.${item.id}`],
        );
        return !itemsChanged;
      }

      if (prevErrors[prevId] !== nextErrors[nextId]) {
        return false;
      }
      return true;
    },
  );
  return ParameterDefinitionRenderer;
}
